import { useEffect, useEffectEvent, useRef } from 'react';

export type ReplaySpeed = 1 | 10 | 30 | 60;

const TICK_MS = 40;
/** Site-ms advanced per real ms, per unit of speed: 0.2 site-minutes per real second. */
const SITE_MS_PER_REAL_MS_PER_SPEED = 0.2 * 60;

interface IUsePlaybackArgs {
  atMs: number;
  endMs: number;
  speed: ReplaySpeed;
  playing: boolean;
  onTick(atMs: number): void;
  onEnd(): void;
}

/** Drives the replay playhead with setInterval (rAF is starved in hidden tabs). */
export function usePlayback({ atMs, endMs, speed, playing, onTick, onEnd }: IUsePlaybackArgs): void {
  const playheadRef = useRef(atMs);
  // The latest handlers, read from the interval without restarting it.
  const tick = useEffectEvent((playhead: number) => onTick(playhead));
  const end = useEffectEvent(() => onEnd());

  useEffect(() => {
    playheadRef.current = atMs;
  }, [atMs]);

  useEffect(() => {
    if (!playing) return undefined;
    let last = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      const next = playheadRef.current + (now - last) * speed * SITE_MS_PER_REAL_MS_PER_SPEED;
      last = now;
      playheadRef.current = Math.min(next, endMs);
      tick(playheadRef.current);
      if (playheadRef.current >= endMs) {
        clearInterval(id);
        end();
      }
    }, TICK_MS);
    return () => clearInterval(id);
  }, [playing, speed, endMs]);
}
